/**
 * 浏览类上报前的联系人门禁。
 * 本地已有 cid / customerId / email / phone / webPush 时不查接口；
 * newsletter / Capture 成功缓存会先灌进本次 payload，再按上报体身份放行（不单独凭无邮箱标记放行）。
 * 匿名：等 oid settle（与 uid 一起）再查 lookup，成功带 oid、失败/超时用空 oid，正常只打网一次；
 * found 后同 uid 不再查。查到才上报，明确未查到不上报；接口异常放行。加购、结账不走这里。
 */
import storage from './storage';
import storageKeys from './storageKeys';
import { whenOidSettled } from './oidSettle';

// lookup 固定打生产域名。测试脚本与正式脚本同一 origin。
var LOOKUP_PATH = '/cartsee/contact/integration/identity/lookup';
var LOOKUP_ORIGIN = 'https://symbol.clink-lists.com';
var CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function getLookupUrl() {
  return LOOKUP_ORIGIN + LOOKUP_PATH;
}

var GATED = {
  'Site/Visit': 1,
  'Site/ViewProduct': 1,
  'Collection/View': 1,
  'Search/Submitted': 1
};

/**
 * 浏览 identity lookup 门禁：匿名 Visit/ViewProduct 等先查存量联系人，查不到不上报。
 * 2026-09-24 曾因部分店铺 flow 量下降临时关闭；2026-09-29 按需重新打开。
 */
var IDENTITY_LOOKUP_ENABLED = true;

var inflight = {};
/** 本页已 lookup 命中的 uid → hit，后续门禁事件不再打网 */
var foundByUid = {};

function text(value) {
  if (value == null) return '';
  var s = String(value).trim();
  if (!s || s === 'null' || s === 'undefined') return '';
  return s;
}

function uidHitKey(payload) {
  return [payload.companyId || '', text(payload.uid)].join('|');
}

function readCaptureOk(companyId) {
  try {
    var raw = storage.getLocalJSON(storageKeys.IDENTITY_CAPTURE_OK, null);
    if (!raw || !raw.t) return null;
    if (Date.now() - Number(raw.t) >= CACHE_TTL_MS) return null;
    if (raw.companyId != null && Number(raw.companyId) > 0 && companyId != null) {
      if (Number(raw.companyId) !== Number(companyId)) return null;
    }
    return raw;
  } catch (e) {
    return null;
  }
}

/**
 * 仅匿名事件：把 newsletter / Capture 缓存的邮箱电话灌进本次上报体。
 * 已有 cid / customerId / email / phone / webPush 时不灌，避免串到已识别用户。
 * 不把 cartx_email（外源扫号）当作门禁放行依据。
 */
function hydratePayloadIdentity(payload) {
  var webPush =
    payload.webPushEndpoint || storage.getLocal(storageKeys.CARTX_WEB_PUSH_ENDPOINT);
  if (
    text(payload.cid) ||
    text(payload.customerId) ||
    text(payload.data && payload.data.email) ||
    text(payload.data && payload.data.phone) ||
    text(webPush)
  ) {
    return;
  }
  if (!payload.data || typeof payload.data !== 'object') payload.data = payload.data || {};
  var data = payload.data;
  if (!text(data.email)) {
    var news = text(storage.getLocal(storageKeys.CARTSEE_NEWSLETTER_EMAIL));
    if (news) data.email = news;
  }
  var cap = readCaptureOk(payload.companyId);
  if (cap) {
    if (!text(data.email) && text(cap.email)) data.email = cap.email;
    if (!text(data.phone) && text(cap.phone)) data.phone = cap.phone;
  }
}

function hasLocalIdentity(identity) {
  return !!(
    text(identity.cid) ||
    text(identity.customerId) ||
    text(identity.email) ||
    text(identity.phone) ||
    text(identity.webPushEndpoint)
  );
}

function cacheKey(payload) {
  return [
    payload.companyId || '',
    payload.integrationId || '',
    text(payload.uid),
    text(payload.oid)
  ].join('|');
}

function readCache() {
  var map = storage.getLocalJSON(storageKeys.IDENTITY_LOOKUP, {}) || {};
  var now = Date.now();
  var next = {};
  for (var k in map) {
    if (!Object.prototype.hasOwnProperty.call(map, k)) continue;
    if (map[k] && now - Number(map[k].t || 0) < CACHE_TTL_MS) next[k] = map[k];
  }
  return next;
}

function writeCache(map) {
  storage.setLocal(storageKeys.IDENTITY_LOOKUP, JSON.stringify(map));
}

function applyFound(payload, hit) {
  if (!hit || !hit.found || !hit.cid) return;
  payload.cid = hit.cid;
  try {
    storage.setLocal(storageKeys.CARTX_CID, hit.cid);
  } catch (e) { /* 写 cid 失败不阻断上报决策 */ }
}

function debugLookup(msg, detail) {
  try {
    var flag = storage.getLocal(storageKeys.DEBUG) || '';
    if (!String(flag).includes('cartsee:')) return;
    console.log('%ccartsee:identity', 'color: #513EFF', msg, detail || '');
  } catch (e) { /* ignore */ }
}

function lookup(payload) {
  return new Promise(function (resolve) {
    try {
      var xhr = new XMLHttpRequest();
      var done = false;
      function finish(hit) {
        if (done) return;
        done = true;
        resolve(hit);
      }
      xhr.open('POST', getLookupUrl(), true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onreadystatechange = function () {
        if (xhr.readyState !== 4) return;
        if (xhr.status === 200) {
          try {
            var body = JSON.parse(xhr.responseText || '{}');
            if (body.code != null && Number(body.code) !== 0) {
              debugLookup('lookup business error', { code: body.code, status: xhr.status });
              finish({ found: true, error: true, t: Date.now() });
              return;
            }
            var data = body.data || {};
            finish({
              found: data.found === true,
              cid: text(data.cid),
              t: Date.now(),
              error: false
            });
            return;
          } catch (e) {
            debugLookup('lookup parse error', e && e.message);
            finish({ found: true, error: true, t: Date.now() });
            return;
          }
        }
        debugLookup('lookup http error', { status: xhr.status });
        finish({ found: true, error: true, t: Date.now() });
      };
      xhr.onerror = function () {
        debugLookup('lookup network error');
        finish({ found: true, error: true, t: Date.now() });
      };
      xhr.send(JSON.stringify({
        companyId: Number(payload.companyId) || 0,
        integrationId: Number(payload.integrationId) || 0,
        uid: text(payload.uid),
        oid: text(payload.oid)
      }));
    } catch (e) {
      resolve({ found: true, error: true, t: Date.now() });
    }
  });
}

/** oid settle 之后：缓存 + lookup；found 后按 uid 停查 */
function lookupAllow(payload) {
  var hitKey = uidHitKey(payload);
  if (text(payload.uid) && foundByUid[hitKey]) {
    applyFound(payload, foundByUid[hitKey]);
    return Promise.resolve(true);
  }

  var key = cacheKey(payload);
  var map = readCache();
  if (map[key]) {
    applyFound(payload, map[key]);
    if (map[key].found && map[key].cid && text(payload.uid)) {
      foundByUid[hitKey] = map[key];
    }
    return Promise.resolve(map[key].error ? true : !!map[key].found);
  }
  if (!inflight[key]) {
    inflight[key] = lookup(payload).then(function (hit) {
      try {
        var latest = readCache();
        if (!hit.error) latest[key] = hit;
        writeCache(latest);
      } catch (e) { /* 缓存失败不阻断上报 */ }
      if (hit && hit.found && hit.cid && text(payload.uid)) {
        foundByUid[hitKey] = hit;
      }
      delete inflight[key];
      return hit;
    });
  }
  return inflight[key].then(function (hit) {
    applyFound(payload, hit);
    return hit.error ? true : !!hit.found;
  }).catch(function () {
    delete inflight[key];
    return true;
  });
}

/** @returns {Promise<boolean>} true=可以继续上报 */
function allow(eventType, payload) {
  try {
    if (!IDENTITY_LOOKUP_ENABLED) return Promise.resolve(true);
    if (!GATED[eventType]) return Promise.resolve(true);
    // 先灌本地 newsletter / Capture 邮箱电话，再按上报体身份判断（避免空身份放行）
    hydratePayloadIdentity(payload);
    var identity = {
      cid: payload.cid,
      customerId: payload.customerId,
      email: payload.data && payload.data.email,
      phone: payload.data && payload.data.phone,
      webPushEndpoint: payload.webPushEndpoint || storage.getLocal(storageKeys.CARTX_WEB_PUSH_ENDPOINT)
    };
    if (hasLocalIdentity(identity)) return Promise.resolve(true);

    var uid = text(payload.uid);
    if (!uid && !text(payload.oid)) return Promise.resolve(false);

    // 等 oid 与 uid 一起查（settle 成功有 oid / 失败或超时为空 oid），不先单独用 uid 查
    return whenOidSettled().then(function (oid) {
      var settledOid = text(oid);
      var payloadOid = text(payload.oid);
      // 空 settle 不得覆盖 payload 上已有 oid（如 _cartsee_send 补报时 cookie 已写入）
      payload.oid = settledOid || payloadOid || '';
      if (!uid && !text(payload.oid)) return false;
      return lookupAllow(payload);
    }).catch(function () {
      return true;
    });
  } catch (e) {
    return Promise.resolve(true);
  }
}

export default { allow: allow, hasLocalIdentity: hasLocalIdentity };
